import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from '@tanstack/react-query';
import { TabsGroup } from '@unified-checkout/design-system';
import type { IPreDefinedRange } from 'libs/design-system/src/types/date_range_picker'
import { useScrollListener } from '@app/dashboard/hooks/useScrollListener';
import { useScreenWidth } from 'libs/design-system/src/hooks/useScreenWidth';
import { useServicesStore } from '@app/dashboard/store/services';
import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';;
import DateRanges from '@app/dashboard/components/inner_layout/DateRanges';
import {
  THeaderProps,
  TTabProps,
} from '@app/dashboard/types/innerLayout/reports';
import { mainPreDefinedRanges as preDefinedRanges } from '@app/dashboard/utils/dateRangePicker';
import { getLang } from '@app/dashboard/utils/localStorageUtils';
import { getFormattedDates } from '@app/dashboard/utils/getFormattedDates';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { REPORTS_TABS } from '@app/dashboard/constants/tracking_events/reports-statements';
import styles from './header.module.scss';

export const Header = ({
  activeTab,
  tabsChangeHandler,
  setDateRange,
}: THeaderProps) => {
  const { t } = useTranslation();
  const lang = getLang();
  const tabsRef = useRef<any>();
  const headerRef = useRef<any>();
  const screenWidth = useScreenWidth();

  const isFetchingReports = useIsFetching({ queryKey: [QUERY_KEYS.GET_REPORTS] });
  const isFetchingArchive = useIsFetching({ queryKey: [QUERY_KEYS.GET_ARCHIVE_REPORTS] });
  const isFetching = isFetchingReports > 0 || isFetchingArchive > 0;

  const [tabsGroupHasOverlay, setTabsGroupHasOverlay] = useState(false);

  const defaultPreDefinedRange: IPreDefinedRange | undefined = preDefinedRanges(
    t
  )?.find((range) => range.default);

  const [appliedRanges, setAppliedRanges] = useState<TAppliedRanges>({
    index: defaultPreDefinedRange?.index,
    name: defaultPreDefinedRange?.name,
    start: defaultPreDefinedRange?.selectionRange[0],
    end: defaultPreDefinedRange?.selectionRange[1],
  });

  const {
    start: startDate,
    end: endDate,
    index: rangeIndex,
    name: rangeName,
  } = appliedRanges;

  useEffect(() => {
    if (startDate && endDate) {
      const formattedDateRange = getFormattedDates([startDate, endDate] as [
        Date,
        Date
      ]);
      setDateRange(formattedDateRange);
    }
  }, [appliedRanges]);

  useEffect(() => {
    handleTabsFading();
  }, [screenWidth]);

  const handleDateRangeChange = (newRanges: TAppliedRanges) => {
    setAppliedRanges(() => ({
      ...newRanges,
    }));
  };

  const handleTabsFading = () => {
    const headerWidth = headerRef?.current.clientWidth;
    const tabsWidth = tabsRef?.current.scrollWidth;
    const tabsScrollX = Math.abs(tabsRef?.current.scrollLeft);
    const smallOffset = 5;

    const reachedToTheEnd =
      tabsWidth - tabsScrollX <= headerWidth + smallOffset;

    setTabsGroupHasOverlay(tabsWidth > headerWidth && !reachedToTheEnd);
  };

  useScrollListener(handleTabsFading, tabsRef);

  const isServicesLoading = useServicesStore((state) => state.isLoading);

  const hasStatements = !isServicesLoading && getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS)?.enabled || false;
  const hasDigest = !isServicesLoading && getFromLocalStorage(LOCAL_STORAGE_KEYS.DIGEST)?.enabled || false;
  const hasReports = isAccessible(PERMISSIONS.CAN_DOWNLOAD_REPORTS);

  const reportsTabsProps: TTabProps[] = [
    ...(hasReports
      ? [
          { id: 'reports', label: t('innerLayout.reportsPage.reports') },
          { id: 'archive', label: t('innerLayout.reportsPage.archive') },
        ]
      : []),
    ...(hasStatements ? [{ id: 'statements', label: t('innerLayout.reportsPage.statements') }] : []),
    ...(hasDigest ? [{ id: 'digest', label: t('innerLayout.reportsPage.digest') }] : []),
    ] as TTabProps[];

  const isReportsOrArchiveActive = activeTab === REPORTS_TABS.REPORTS || activeTab === REPORTS_TABS.ARCHIVE;

  return (
    <div className={styles['header']} ref={headerRef}>
      <div className={styles['header__title']}>
        {t('innerLayout.navbar.reports')}
      </div>
      {isReportsOrArchiveActive && 
      <DateRanges
        appliedRanges={appliedRanges}
        handleRangesChange={(ranges) => handleDateRangeChange(ranges)}
        lang={lang}
        disabled={isFetching}
        />}
      <div
        className={`${styles['header__tabs']} ${
          tabsGroupHasOverlay ? styles['header__tabs--faded'] : ''
        }`}
      >
        <div className={styles['header__tabs__group-wrapper']} ref={tabsRef}>
          <TabsGroup
            type="line"
            orientation="horizontal"
            tabsProps={reportsTabsProps.map((tab, index) => ({
              active: activeTab === tab.id,
              disabled: false,
              tabLabel: tab.label,
              onClickHandler: () => tabsChangeHandler(tab.id),
            }))}
          />
        </div>
      </div>
    </div>
  );
};
